import { useAppDispatch, useAppSelector } from "@/lib/store";
import { useEffect } from "react";
import type { MembershipRole, MembershipStatus, SettableMemberStatus } from "@shyne/shared/api";
import {
  fetchTeamMembers,
  inviteTeamMember,
  sendTeamMemberUpdate,
  setEditingMember,
  setEditModalOpen,
  setPage,
  setPageSize,
  setRole,
  setSortKey,
  setStatus,
} from "./teammembers.slice";
import type { TeamMember } from "@/repositories/team-members.repository.ts";
import { toast } from "sonner";
import { useAdministersActiveOrganization } from "@/hooks/useAdministersActiveOrganization";
import { useActiveOrganizationType } from "@/hooks/useActiveOrganizationType";

export const useTeamMembers = () => {
  const dispatch = useAppDispatch();
  const { list, loading, error, total, filters, pagination, editingModalOpen, editingMember } = useAppSelector((state) => state.teamMembers);
  const activeOrgId = useAppSelector((state) => state.organization.activeOrgId);
  const administers = useAdministersActiveOrganization();
  const organizationType = useActiveOrganizationType();

  useEffect(() => {
    if (activeOrgId) dispatch(fetchTeamMembers());
  }, [dispatch, activeOrgId, filters.role, filters.status, pagination.page, pagination.pageSize, filters.sortKey, filters.sortOrder]);

  const invite = async (name: string, email: string, role: MembershipRole) => {
    try {
      await dispatch(inviteTeamMember({ name, email, role })).unwrap();
      toast.success(`Invite to ${email} has been sent.`);
    } catch (e: any) {
      toast.error(e.message);
    }
    dispatch(fetchTeamMembers());
  };

  const handleChangeRole = (member: TeamMember) => {
    dispatch(setEditingMember(member));
    dispatch(setEditModalOpen(true));
  };

  const saveRoleStatus = async (role: MembershipRole, status: SettableMemberStatus | undefined) => {
    if (!editingMember) return;

    try {
      await dispatch(sendTeamMemberUpdate({ member: editingMember, role, status })).unwrap();
      toast.success("Saved.");
    } catch (e: any) {
      toast.error(e.message);
    }
    dispatch(setEditingMember(null));
    dispatch(setEditModalOpen(false));
    dispatch(fetchTeamMembers());
  };

  const setEditingModalOpen = (open: boolean) => {
    dispatch(setEditModalOpen(open));
    if (!open) dispatch(setEditingMember(null));
  };

  return {
    members: list,
    loading,
    error,
    total,
    administers,
    organizationType,
    invite,
    editingMember,
    editingModalOpen,
    saveRoleStatus,
    setEditingModalOpen,
    filters: {
      ...filters,
      setRole: (r: MembershipRole | "") => dispatch(setRole(r)),
      setStatus: (s: MembershipStatus | "") => dispatch(setStatus(s)),
      setSortKey: (key: string) => dispatch(setSortKey(key)),
    },
    pagination: {
      ...pagination,
      setPage: (p: number) => dispatch(setPage(p)),
      setPageSize: (s: number) => dispatch(setPageSize(s)),
    },
    handleChangeRole,
  };
};
